// shared/components/molecules/ui-molecule-mat-table/ui-molecule-mat-table.component.ts

import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TableColumn } from './interfaces/table-column';
import { TableAction } from './interfaces/table-action';
import { ActionEvent } from './interfaces/action-event';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { UiMoleculePaginatorComponent } from '../ui-molecule-paginator/ui-molecule-paginator.component';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { MatProgressBar } from '@angular/material/progress-bar';
import { UiMoleculeSpinnerComponent } from '../ui-molecule-spinner/ui-molecule-spinner.component';
import { UiAtomButtonIconComponent } from '../../atoms/ui-atom-button-icon/ui-atom-button-icon.component';
import { UiAtomStatusChipComponent } from '../../atoms/ui-atom-status-chip/ui-atom-status-chip.component';
import { UiConfig } from '@core/config/ui.config';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ExcelExportService } from '@core/services/utils/excel-export.service';
import { FormatPhonePipe } from '../../../pipes/format-phone.pipe';
import { DialogExportColumnsService } from '@shared/services/dialog-export-columns.service';
import { DialogExportProgressService, ActiveExportDialog } from '@shared/services/dialog-export-progress.service';
import { SnackbarService } from '@shared/services/snackbar.service';

export interface ExportWithColumnsEvent {
  selectedKeys: string[];
}

@Component({
  selector: 'ui-molecule-mat-table',
  standalone: true,
  imports: [
    MatTable,
    MatSort,
    MatHeaderCell,
    MatColumnDef,
    MatHeaderCellDef,
    FormsModule,
    MatInput,
    MatCell,
    DatePipe,
    MatTooltip,
    MatHeaderRow,
    MatRow,
    MatHeaderRowDef,
    MatRowDef,
    MatProgressBar,
    UiMoleculePaginatorComponent,
    UiMoleculeSpinnerComponent,
    MatCellDef,
    MatSortHeader,
    UiAtomButtonIconComponent,
    UiAtomStatusChipComponent,
    TranslateModule,
    FormatPhonePipe,
  ],
  templateUrl: './ui-molecule-mat-table.component.html',
  styleUrl: './ui-molecule-mat-table.component.scss',
})
export class UiMoleculeMatTableComponent implements OnInit, AfterViewInit, OnChanges {
  /* ------------ Inputs ------------ */
  @Input() data: any[] = [];
  @Input() columns: TableColumn[] = [];
  @Input() paginator = false;

  /** DUAL PAGINATION */
  @Input() paginationMode: 'client' | 'server' = 'client';
  /** Longitud total (solo server) */
  @Input() totalItems: number = 0;

  /** Estado de paginación (se refleja en el paginator hijo) */
  @Input() pageIndex: number = 0;
  @Input() pageSize: number = 10;

  @Input() actions: TableAction[] = [];
  @Input() noDataText: string = 'No hay datos disponibles';
  @Input() spinnerColor: 'primary' | 'accent' | 'warn' = 'accent';
  @Input() isLoading = false;

  /** Ordenación por defecto */
  @Input() defaultSortDirection: 'asc' | 'desc' | 'none' = 'none';
  @Input() defaultSortColumn?: string;

  /** Filtro adicional (cliente) */
  @Input() additionalFilter?: (row: any) => boolean;

  /** Filtros de cabecera */
  @Input() showClearFiltersButton: boolean = false;
  /** Mostrar botón de exportar a Excel */
  @Input() showExportButton: boolean = true;
  /** Mostrar buscador global en el paginator */
  @Input() showGlobalSearch: boolean = true;
  /** Nombre del archivo Excel a exportar */
  @Input() exportFileName: string = 'export';
  @Input() filterValues: { [key: string]: string } = {};

  /** Activa el nuevo flujo: diálogo selector de columnas + web worker + progreso */
  @Input() useExportColumnPicker: boolean = false;

  /** Título de la columna de acciones (fallback). Si no se pasa, se usa i18n tables.columns.actions */
  @Input() actionsColumnTitle?: string;
  /** Clave i18n para la columna de acciones */
  @Input() actionsColumnTitleKey: string = 'tables.columns.actions';

  /* ------------ Outputs ------------ */
  @Output() actionClick = new EventEmitter<ActionEvent>();
  @Output() rowClick = new EventEmitter<any>();
  @Output() filtersReset = new EventEmitter<void>();

  /** Eventos hacia el padre en modo server */
  @Output() pageChange = new EventEmitter<{ pageIndex: number; pageSize: number }>();
  @Output() sortChange = new EventEmitter<Sort>();
  @Output() filterChange = new EventEmitter<Record<string, string>>();
  @Output() globalSearch = new EventEmitter<string>();

  /** Legacy: export sin selector de columnas (tablas que NO usan useExportColumnPicker) */
  @Output() exportAllData = new EventEmitter<void>();

  /** Nuevo: export con selector de columnas. El padre descarga todos los datos y llama exportFilteredDataToExcel() */
  @Output() exportAllDataWithColumns = new EventEmitter<ExportWithColumnsEvent>();

  /** Tiempo de muestra del tooltip */
  @Input() tooltipShowDelay = UiConfig.tooltip.showDelay;

  /* ------------ ViewChild ------------ */
  @ViewChild(MatSort) private sort!: MatSort;
  @ViewChild(UiMoleculePaginatorComponent) private customPaginator!: UiMoleculePaginatorComponent;

  /* ------------ Estado interno ------------ */
  private initialFilterValues: { [key: string]: string } = {};
  protected dataSource = new MatTableDataSource<any>([]);
  protected displayedColumns: string[] = [];

  private activeExportDialog?: ActiveExportDialog;

  /** getter accesible desde template */
  protected get isServer(): boolean {
    return this.paginationMode === 'server';
  }

  protected get exportLabelKey(): string {
    return this.useExportColumnPicker ? 'tables.actions.exportList' : 'tables.actions.export';
  }

  protected get exportTooltipKey(): string {
    return this.useExportColumnPicker ? 'tables.actions.exportList' : 'tables.actions.exportExcel';
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private excelExportService: ExcelExportService,
    private dialogExportColumns: DialogExportColumnsService,
    private dialogExportProgress: DialogExportProgressService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
  ) {}

  /* ------------ Ciclo de vida ------------ */

  ngOnInit(): void {
    this.initColumns();
    this.dataSource.data = Array.isArray(this.data) ? [...this.data] : [];
    this.initialFilterValues = { ...this.filterValues };
    this.setupClientFiltering();
  }

  ngAfterViewInit(): void {
    if (this.sort) {
      this.dataSource.sort = this.sort;
      this.sort.sortChange.subscribe((s) => {
        if (this.isServer) this.sortChange.emit(s);
      });
    }
    this.applyDefaultSort();

    if (this.paginator && this.customPaginator) {
      if (!this.isServer) {
        this.dataSource.paginator = this.customPaginator.getPaginator();
      }
    }
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.dataSource.data = Array.isArray(this.data) ? [...this.data] : [];
    }

    if (changes['paginationMode'] && !changes['paginationMode'].firstChange) {
      if (this.sort) this.dataSource.sort = this.sort;

      if (this.isServer) {
        this.dataSource.paginator = null as any;
      } else {
        if (this.customPaginator?.getPaginator())
          this.dataSource.paginator = this.customPaginator.getPaginator();
        this.setupClientFiltering();
      }
    }

    if (!this.isServer && changes['data'] && this.customPaginator?.getPaginator()) {
      this.dataSource.paginator = this.customPaginator.getPaginator();
    }
  }

  /* ------------ API pública ------------ */

  public onload(): void {
    if (this.paginator && this.customPaginator && !this.isServer) {
      this.dataSource.paginator = this.customPaginator.getPaginator();
    }
    this.dataSource.data = Array.isArray(this.data) ? [...this.data] : [];
  }

  public updateTableData(newData: any[]): void {
    this.dataSource.data = Array.isArray(newData) ? [...newData] : [];
    if (!this.isServer && this.customPaginator?.getPaginator()) {
      this.dataSource.paginator = this.customPaginator.getPaginator();
    }
  }

  public clearFilters(): void {
    this.filterValues = { ...this.initialFilterValues };
    if (!this.isServer) {
      this.dataSource.filter = '';
      if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
    } else {
      this.filterChange.emit({ ...this.filterValues });
    }
    this.filtersReset.emit();
  }

  public onGlobalSearch(searchTerm: string): void {
    if (searchTerm && searchTerm.trim().length >= 3) {
      if (!this.isServer) {
        this.dataSource.filterPredicate = (data: any, filter: string) => {
          const searchStr = filter.toLowerCase();
          return this.columns.some((column) => {
            const value = this.getNestedValue(data, column.key);
            if (value === null || value === undefined) return false;
            return value.toString().toLowerCase().includes(searchStr);
          });
        };
        this.dataSource.filter = searchTerm.trim().toLowerCase();
        if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
      }
      this.globalSearch.emit(searchTerm);
    } else if (!searchTerm || searchTerm.trim() === '') {
      if (!this.isServer) {
        this.dataSource.filter = '';
        if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
      }
      this.globalSearch.emit('');
    }
  }

  /** Exportar datos a Excel */
  public exportToExcel(): void {
    if (!this.useExportColumnPicker) {
      // Flujo legacy (tablas sin selector de columnas, ej. Users)
      if (this.isServer) {
        this.exportAllData.emit();
        return;
      }
      const dataToExport =
        this.dataSource.filteredData.length > 0 ? this.dataSource.filteredData : this.dataSource.data;
      this.excelExportService.exportToExcel(dataToExport, this.columns, this.exportFileName);
      return;
    }

    // Nuevo flujo: selector de columnas + worker + progreso
    const exportableColumns = this.columns.filter(
      (c) => c.key !== 'actions' && c.type !== 'actions' && c.type !== 'icon-action',
    );

    this.dialogExportColumns.open(exportableColumns).subscribe((selectedKeys) => {
      if (!selectedKeys || selectedKeys.length === 0) return;

      this.activeExportDialog = this.dialogExportProgress.open();

      if (this.isServer) {
        this.exportAllDataWithColumns.emit({ selectedKeys });
      } else {
        const data =
          this.dataSource.filteredData.length > 0
            ? this.dataSource.filteredData
            : this.dataSource.data;
        this.runExport(data, selectedKeys);
      }
    });
  }

  /** Llamado por el padre tras obtener todos los datos del backend (modo server) */
  public exportFilteredDataToExcel(allData: any[], selectedKeys: string[]): void {
    this.runExport(allData, selectedKeys);
  }

  /** @deprecated Usar exportFilteredDataToExcel() en tablas con useExportColumnPicker */
  public exportAllDataToExcel(allData: any[]): void {
    this.excelExportService.exportToExcel(allData, this.columns, this.exportFileName);
  }

  /* ------------ Filtros ------------ */

  public applyFilter(): void {
    if (this.isServer) {
      this.filterChange.emit({ ...this.filterValues });
      return;
    }
    this.setupClientFiltering();
    this.dataSource.filter = JSON.stringify(this.filterValues);
    if (this.dataSource.paginator) this.dataSource.paginator.firstPage();
  }

  private setupClientFiltering(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterObj = JSON.parse(filter || '{}');
      const matchesColumns = Object.keys(filterObj).every((key) => {
        const filterValue = filterObj[key] ?? '';
        const column = this.columns.find((c) => c.key === key);
        if (column?.filterType === 'dropdown') {
          return filterValue ? data[key]?.toString() === filterValue : true;
        } else {
          return (data[key]?.toString() ?? '')
            .toLowerCase()
            .includes(String(filterValue).toLowerCase());
        }
      });
      const passesAdditional = this.additionalFilter ? this.additionalFilter(data) : true;
      return matchesColumns && passesAdditional;
    };
  }

  /* ------------ Sort por defecto ------------ */

  private applyDefaultSort(): void {
    if (!this.sort || this.defaultSortDirection === 'none') return;
    const defaultColumn = this.defaultSortColumn
      ? this.columns.find((col) => col.key === this.defaultSortColumn)
      : this.columns.find((col) => col.type === 'date');
    if (!defaultColumn) return;
    this.sort.active = defaultColumn.key;
    this.sort.direction = this.defaultSortDirection;
    if (this.isServer) return;
    const initialSort: Sort = { active: defaultColumn.key, direction: this.defaultSortDirection };
    this.sort.sortChange.emit(initialSort);
  }

  /* ------------ Eventos de fila/acción ------------ */

  protected onActionClick(event: string, row: any): void {
    this.actionClick.emit({ event, rowData: row });
    this.cdr.detectChanges();
  }

  protected onRowClick(row: any): void {
    this.rowClick.emit(row);
    this.cdr.detectChanges();
  }

  /**
   * Copia al portapapeles el valor de una celda `copy-text` y notifica con un snackbar.
   * El click de la celda hace `stopPropagation()` en el template para no disparar
   * la navegación de fila (`onRowClick`).
   */
  protected onCopyClick(value: unknown): void {
    if (value === null || value === undefined || value === '') return;
    navigator.clipboard
      .writeText(String(value))
      .then(() => {
        this.snackbar.open(this.translate.instant('tables.messages.copiedToClipboard'));
      })
      .catch(() => {
        this.snackbar.open(this.translate.instant('tables.messages.copyFailed'));
      });
  }

  /* ------------ Utilidades ------------ */

  protected getNestedValue(obj: any, key: string): any {
    return key.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : null), obj);
  }

  private initColumns(): void {
    const regularCols = this.columns.filter((c) => c.type !== 'icon-action').map((c) => c.key);
    const iconActionCols = this.columns.filter((c) => c.type === 'icon-action').map((c) => c.key);
    this.displayedColumns = [
      ...regularCols,
      ...(this.actions.length > 0 ? ['actions'] : []),
      ...iconActionCols,
    ];
  }

  protected onInternalPageChange(ev: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = ev.pageIndex;
    this.pageSize = ev.pageSize;
    this.pageChange.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize });
  }

  private runExport(data: any[], selectedKeys: string[]): void {
    if (!this.activeExportDialog) return;
    const { progress$, cancel$, close } = this.activeExportDialog;

    this.excelExportService
      .exportWithWorker(data, this.columns, selectedKeys, this.exportFileName, progress$, cancel$)
      .subscribe({
        next: () => {
          close();
          this.activeExportDialog = undefined;
        },
        error: () => {
          close();
          this.activeExportDialog = undefined;
          this.snackbar.open(this.translate.instant('exportProgress.dialog.error'));
        },
      });
  }
}
